import Box, { BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { FlexBox } from '../layout';

interface NavigatableWrapperProps extends BoxProps {
  scrollAmount?: number;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const NavigatableWrapper: React.FC<
  PropsWithChildren<NavigatableWrapperProps>
> = ({ children, scrollAmount = 200, leftIcon, rightIcon, ...props }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [arrowState, setArrowState] = useState({
    showLeft: false,
    showRight: false,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      setArrowState({
        showLeft: container.scrollLeft > 0,
        showRight:
          container.scrollLeft < container.scrollWidth - container.clientWidth,
      });
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLeftClick = () =>
    containerRef.current?.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });

  const handleRightClick = () =>
    containerRef.current?.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

  return (
    <Box
      {...props}
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      <FlexBox justifyContent="flex-end" gap={1} mb={1}>
        <IconButton
          onClick={handleLeftClick}
          disabled={arrowState.showLeft === false}
          size="small"
        >
          {leftIcon ?? <ChevronLeftIcon />}
        </IconButton>
        <IconButton
          onClick={handleRightClick}
          disabled={arrowState.showRight === false}
          size="small"
        >
          {rightIcon ?? <ChevronRightIcon />}
        </IconButton>
      </FlexBox>
      <FlexBox>
        <Box
          ref={containerRef}
          sx={{
            overflow: 'auto',
            display: 'flex',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {children}
        </Box>
      </FlexBox>
    </Box>
  );
};

export default NavigatableWrapper;
